import { useMutation } from "@tanstack/react-query";
import { Button, Input, message, Spin } from "antd";
import { FC, useState } from "react";
import { useIntl } from "react-intl";
import { updateAppointment } from "../../../../network/calendar";
import queryClient from "../../../../services/react-query/queryClient";
import { appointmentDetails } from "../../../../types/calendar";
import { LoadingOutlined } from "@ant-design/icons";

interface TipsProps {
  appointment: appointmentDetails;
}

const Tips: FC<TipsProps> = ({ appointment }) => {
  const intl = useIntl();
  const [selectedTips, setSelectedTips] = useState<number | null>(
    +Number(appointment?.tips_amount).toFixed() || null
  );
  const tipsValues = [10, 20, 30, 50, 60, 70];
  const { mutate, isLoading } = useMutation(
    (body: any) =>
      updateAppointment(appointment?.location_id, appointment?.id, body),
    {
      onSuccess: (res: any) => {
        if (res?.code !== "ERR_BAD_REQUEST") {
          message.success(
            intl.formatMessage({
              id: !!selectedTips
                ? "tipsAddedSuccessfully"
                : "tipsRemovedSuccessfully",
            })
          );
          queryClient.invalidateQueries(["appointment"]);
        }
      },
      onError: () => {
        message.error(intl.formatMessage({ id: "somethingWentWrong" }));
      },
    }
  );

  return (
    <div className="space-y-8">
      <div className="text-center space-y-4">
        <h3 className="text-color-primary text-3xl font-semibold">
          {intl.formatMessage({
            id: Number(appointment?.tips_amount) ? "theTips" : "addTips",
          })}
        </h3>
        <p className="text-color-sec-title text-xl">
          {intl.formatMessage({
            id: Number(appointment?.tips_amount)
              ? "tipsAdded"
              : "chooseTipsDesc",
          })}
        </p>
      </div>
      {Number(appointment?.tips_amount) ? (
        <div className="flex gap-4 flex-wrap  justify-center items-center">
          <div className="flex bg-[#479821] px-4 py-2 gap-4 rounded-lg text-white justify-between min-w-[160px]">
            <span className="text-xl ">
              {Number(appointment?.tips_amount).toFixed()}
              <sub className="text-xs ms-px">
                {intl.formatMessage({ id: "SAR" })}
              </sub>
            </span>
            {isLoading ? (
              <Spin
                indicator={<LoadingOutlined className="mt-2 text-white" spin />}
                size="small"
              />
            ) : (
              <span
                className="text-2xl text-white cursor-pointer "
                onClick={() => {
                  setSelectedTips(null);
                  mutate({
                    tips_amount: 0,
                  });
                }}
              >
                &times;
              </span>
            )}
          </div>
        </div>
      ) : (
        <>
          <div className="flex gap-4 flex-wrap  justify-center items-center">
            {tipsValues.map((value, i) => {
              return (
                <div key={i}>
                  <Button
                    type={selectedTips === value ? "primary" : "default"}
                    htmlType="button"
                    onClick={() => {
                      setSelectedTips(value);
                    }}
                    className={`border-none w-[120px] h-[50px]  rounded-full  font-semibold ${
                      value === selectedTips
                        ? ""
                        : "text-color-sec-title hover:border-solid bg-[#EEF0FF]"
                    }`}
                  >
                    <span className="text-xl ">
                      {value}
                      <sub className="text-xs ms-px">
                        {intl.formatMessage({ id: "SAR" })}
                      </sub>
                    </span>
                  </Button>
                </div>
              );
            })}
          </div>
          {selectedTips ? (
            <div className="flex justify-between gap-4">
              <Button
                type="primary"
                block
                onClick={() => {
                  mutate({
                    tips_amount: selectedTips,
                  });
                }}
                loading={isLoading}
              >
                {intl.formatMessage({ id: "sendTips" })}
              </Button>
              <Button
                type="default"
                block
                onClick={() => {
                  mutate({
                    tips_amount: 0,
                  });
                  setSelectedTips(null);
                }}
                disabled={isLoading}
              >
                {intl.formatMessage({ id: "cancel" })}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
};

export default Tips;
